<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Code from '@/dashboard/components/Code.vue';

const props = defineProps({
  record: {
    type: Object,
    default: () => ({}),
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['deleteRecord']);

const record = computed(() => {
  const data = props.record;
  return data;
});

const isJsonType = computed(() => {
  return record.value?.type === 'json' || record.value?.jblob;
});

const jsonString = computed(() => {
  if (!record.value?.jblob) return '';
  return JSON.stringify(record.value.jblob, null, 2);
});

const isPreviewable = extension => ['pdf', 'txt', 'md'].includes(extension);

function open(file) {
  window.open(file.url, '_blank');
}
</script>

<template>
  <div>
    <div v-if="record" class="p-8 rounded">
      <div
        v-if="isJsonType"
        class="border-2 rounded-lg overflow-hidden shadow-lg bg-white mx-auto"
      >
        <div
          class="bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 rounded-t-lg flex items-center justify-between align-baseline"
        >
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-braces" :size="16" />
            <span>{{ 'JSON Data' }}</span>
          </div>
        </div>
        <div class="p-4 bg-white">
          <Code :script="jsonString" lang="json" />
        </div>
      </div>
      <div
        v-else-if="record.file || record.source"
        class="border-2 rounded-lg overflow-hidden shadow-lg bg-white mx-auto"
      >
        <div
          class="bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 rounded-t-lg flex items-center justify-between align-baseline"
        >
          <input
            type="text"
            :value="record.file?.filename || record.source"
            readonly
            class="w-full bg-slate-200 text-n-gray-700 !mb-0 px-2 py-1 rounded-md border border-gray-300 focus:outline-none"
          />
          <button
            class="button ml-2"
            @click="open(record.file || { url: record.source })"
          >
            <Icon icon="i-lucide-external-link" :size="48" />
          </button>
        </div>
        <iframe
          v-if="record.file && isPreviewable(record.file.extension)"
          :src="record.file.url"
          class="w-full h-[612px] border-t"
        />
        <iframe
          v-else-if="record.source"
          :src="record.source"
          class="w-full h-[612px] border-t"
          sandbox="allow-same-origin allow-scripts"
        />
        <p v-else class="p-4">
          {{ $t('LIBRARIES.RECORDS.PREVIEW.UNSUPPORTED_FILE_TYPE') }}
        </p>
      </div>
      <div class="flex items-center flex-row mt-2">
        <p class="text-sm text-gray-600 mb-0 w-full">
          {{ $t('LIBRARIES.RECORDS.UPDATED_AT.LABEL') }}:
          <span class="ml-1">{{
            new Date(record.updated_at).toLocaleString()
          }}</span>
        </p>
        <NextButton
          v-if="!compact"
          v-tooltip.top-middle="$t('LIBRARIES.RECORDS.REMOVE.LABEL')"
          icon="i-lucide-trash-2"
          variant="danger"
          sm
          @click="$emit('deleteRecord', record)"
        >
          {{ $t('LIBRARIES.RECORDS.REMOVE.LABEL') }}
        </NextButton>
      </div>
    </div>
    <div v-else class="text-gray-500">
      {{ $t('LIBRARIES.RECORDS.PREVIEW.NO_RECORD') }}
    </div>
  </div>
</template>
